import React from 'react';
import _ from 'lodash';
import moment from 'moment';
import {connect} from 'react-redux';
import {reduxForm, submit, Field} from 'redux-form';
import classNames from 'classnames';

import {MoneyField, TextField, SearchField, DateField} from 'components/Fields';
import FormField from 'components/FormField';
import Label from 'components/Label';
import ErrorBlock from 'components/ErrorBlock';
import Button from 'components/Button';

import {required, validDate} from 'utils/validators';
import {formatDateForApi, getCurrentMonthFirstDay} from 'utils/datetime';
import {
  MODALS,
  ACCOUNT_TYPES,
  FETCH_SCHEDULE_DELAY,
  addRefundForOverpayment,
  fetchXeroAccounts,
  fetchCanCreateFinanceItemForPastMonths
} from 'ducks/tenancy';
import {selectModal} from 'ducks/modals';
import {delayedAction, composeActions} from 'ducks/common';

import './styles.css';

const formSubmit = (values, dispatch, props) => {
  const data = {
    ...values,
    tenancyId: props.tenancyId,
    overpayment: props.overpaymentId,
    xeroAccount: _.get(values, 'xeroAccount.id'),
    paymentDate: formatDateForApi(values.paymentDate)
  };

  const delayedFetchSchedule = () =>
    delayedAction({
      delay: FETCH_SCHEDULE_DELAY,
      action: () =>
        composeActions(
          props.refetchExtraActions({
            tenancyId: props.tenancyId,
            scheduleType: props.scheduleType
          })
        )
    });

  const extraActions = [...props.getExtraActions(), delayedFetchSchedule];
  dispatch(addRefundForOverpayment(data, extraActions));
};

class ReceiveCashRefundForm extends React.Component<any> {
  componentDidMount() {
    const {locationRef} = this.props;

    const fetchXeroAccountsExtraAction = this.props.storeDataExtraActions(
      data => ({
        xeroAccounts: data
      })
    );
    this.props.fetchXeroAccounts(
      {
        accountType: ACCOUNT_TYPES.BANK,
        location: locationRef
      },
      fetchXeroAccountsExtraAction
    );
    const fetchCanCreateFinanceItemForPastMonthsExtraAction = this.props.storeDataExtraActions(
      data => ({
        canCreateCashRefundForPastMonths: _.get(
          data,
          'canCreateFinanceItemsForPastMonths'
        )
      })
    );

    this.props.fetchCanCreateFinanceItemForPastMonths(
      {locationRef: this.props.locationRef},
      fetchCanCreateFinanceItemForPastMonthsExtraAction
    );
  }
  render() {
    const {
      className,
      handleSubmit,
      submitting,
      dispatch,
      error,
      xeroAccounts,
      canCreateCashRefundForPastMonths
    } = this.props;
    const triggerSubmit = () => {
      dispatch(submit('receiveCashRefundForOverpayment'));
    };
    const xeroAccountOptions =
      xeroAccounts &&
      xeroAccounts.map(acc => ({
        value: `${acc.id} - ${acc.label}`,
        label: acc.label,
        id: acc.id
      }));
    let datepickerProps: any = undefined;
    const currentMonthFirstDay: any = getCurrentMonthFirstDay();

    if (!canCreateCashRefundForPastMonths) {
      datepickerProps = {
        disabledDays: {before: new Date(currentMonthFirstDay)}
      };
    }
    const DatePickerWithPermissionsOverlay = ({
      children,
      onBlur,
      onFocus,
      tabIndex,
      month // month here is the first day of the current selected month
    }: any) => {
      const shouldShowWarning =
        !canCreateCashRefundForPastMonths &&
        moment(month) < currentMonthFirstDay;

      return (
        <div
          className="DayPickerInput-OverlayWrapper"
          onBlur={onBlur}
          onFocus={onFocus}
          tabIndex={tabIndex}>
          <div className="DayPickerInput-Overlay">
            {children}
            {shouldShowWarning && (
              <div className="colab-centered colab-bold ">
                You do not have the permission!
              </div>
            )}
          </div>
        </div>
      );
    };

    return (
      <div className="formContainer">
        {error && <ErrorBlock>{error}</ErrorBlock>}
        <form
          onSubmit={handleSubmit}
          className={classNames(className, 'ReceiveCashRefundForm')}>
          <div className="fields">
            <FormField>
              <Label>Amount paid</Label>
              <Field
                name="amount"
                validate={[required]}
                component={MoneyField}
              />
            </FormField>
            <FormField>
              <Label>Date paid</Label>
              <Field
                name="paymentDate"
                component={DateField}
                validate={[validDate]}
                dayPickerProps={datepickerProps}
                overlayComponent={DatePickerWithPermissionsOverlay}
              />
            </FormField>
            <FormField>
              <Label>Paid to</Label>
              <Field
                name="xeroAccount"
                placeholder="Select option..."
                validate={[required]}
                component={SearchField}
                options={xeroAccountOptions}
              />
            </FormField>
            <FormField>
              <Label>Reference</Label>
              <Field
                placeholder="Reference"
                name="reference"
                validate={[required]}
                component={TextField}
              />
            </FormField>
          </div>
          <Button
            onClick={triggerSubmit}
            blue={!submitting}
            isLoading={submitting}
            disabled={submitting}>
            Add Refund
          </Button>
        </form>
      </div>
    );
  }
}

const mapStateToProps = state => {
  const {data: {xeroAccounts, canCreateCashRefundForPastMonths}} = selectModal(
    state,
    MODALS.OVERPAYMENT_DETAIL
  );
  return {
    xeroAccounts,
    canCreateCashRefundForPastMonths
  };
};

const mapDispatchToProps = {
  fetchXeroAccounts,
  fetchCanCreateFinanceItemForPastMonths
};

export default connect(mapStateToProps, mapDispatchToProps)(
  reduxForm({
    form: 'receiveCashRefundForOverpayment',
    onSubmit: formSubmit
  })(ReceiveCashRefundForm)
);
